import React, { useState, useEffect, useMemo } from "react";
import {
  Box,
  VStack,
  HStack,
  Text,
  Button,
  Flex,
  Icon,
  Input,
  Textarea,
  FormControl,
  FormLabel,
  IconButton,
  Spinner,
  Center,
  Collapse,
  Badge,
  Menu,
  MenuButton,
  MenuList,
  MenuItem,
  Portal,
  Stack,
  Tabs,
  TabList,
  TabPanels,
  Tab,
  TabPanel,
  Tooltip,
  Modal,
  ModalOverlay,
  ModalContent,
  ModalHeader,
  ModalFooter,
  ModalBody,
  ModalCloseButton,
  useDisclosure,
} from "@chakra-ui/react";
import { ChevronDownIcon, CloseIcon, InfoIcon } from "@chakra-ui/icons";
import { FiDatabase, FiGrid, FiTarget } from "react-icons/fi";
import { MdBarChart } from "react-icons/md";
import { FaFilter, FaRegWindowMinimize, FaCheck, FaTable, FaPlay, FaHistory } from "react-icons/fa";
import { AiFillPieChart } from "react-icons/ai";
import { LuScrollText } from "react-icons/lu";
import { Header, MetricProto, FilterProto, ObservationColumnProto, ErrorDetailProto } from "build-ts/metadata";
import { BenchmarkEntityProto } from "build-ts/knowledege_graph";
import { ColumnHashProto } from "build-ts/data";
import { ColumnType, MetadataTypeEnum, TimeBucket } from "build-ts/enums";
import { listMetrics, getAllMetrics } from "services/MetricService";
import { getKGGrowthBenchmarks, mergeMetricEdgesByType } from "utils/analysisUtils";
import { formatToSentenceCase } from "utils/nameUtils";
import showToast from "hooks/useCustomToast";
import MetricSelection from "components/analyse/MetricsSelection";
import Filters from "components/data_query/Filters";
import Benchmarks from "components/data_query/Benchmarks";
import { AnalysisPlanProto, AnalysisStepProto, KGObservationProto } from "build-ts/analysis_plan";
import { v4 as uuidv4 } from "uuid";
import { createOrUpdatePlan, getPlan } from "services/AnalysisPlanServices";
import { getKGObservationData } from "services/AnalysisTreeServices";
import { NodeInsights } from "containers/Interfaces";
import { AnalysisTreeNodeProto } from "build-ts/analysis";
import DataTableComponent from "containers/analysis-plan/DataTableComponent";
import { bifurcateBenchmarkColumnHashesAndAnalysisColumns } from "utils/analysisPlanUtils";
import { useParams } from "react-router-dom";
import DataQueryList from "components/data_query/DataQueryList";

// Define section types
type SectionType = "filters" | "attributes" | "benchmarks" | "analysis" | null;

const DataConfiguratorContainer: React.FC = () => {
  // Get dataQueryId from URL params
  const { id: dataQueryId } = useParams<{ id?: string }>();
 
  // Track if we're editing an existing data query
  const [existingDataQueryId, setExistingDataQueryId] = useState<string | undefined>(dataQueryId);
  const [isLoadingDataQuery, setIsLoadingDataQuery] = useState<boolean>(!!dataQueryId);

  // Basic info states
  const [name, setName] = useState<string>("");
  const [description, setDescription] = useState<string>("");

  // Tab states
  const [activeTab, setActiveTab] = useState<number>(0);
  const [isDataTableEnabled, setIsDataTableEnabled] = useState<boolean>(false);

  // Metrics states
  const [listMetricsHeaders, setListMetricsHeaders] = useState<Header[]>([]);
  const [selectedMetricHeaders, setSelectedMetricHeaders] = useState<Header[]>([]);
  const [selectedMetrics, setSelectedMetrics] = useState<MetricProto[]>([]);
  const [isLoadingMetrics, setIsLoadingMetrics] = useState<boolean>(false);
  const [isFetchingMetricDetails, setIsFetchingMetricDetails] = useState<boolean>(false);

  // Collapsible section states
  const [isMetricsExpanded, setIsMetricsExpanded] = useState<boolean>(true);

  // Active section for sidebar navigation (null = no section selected)
  const [activeSection, setActiveSection] = useState<SectionType>(null);

  // Configuration states
  const [filters, setFilters] = useState<FilterProto[]>([]);
  const [selectedColumns, setSelectedColumns] = useState<ObservationColumnProto[]>([]);
  const [benchmarks, setBenchmarks] = useState<BenchmarkEntityProto[]>([]);
  const [benchmarkColumnHashes, setBenchmarkColumnHashes] = useState<ColumnHashProto[]>([]);

  // Column dropdown selection state
  const [selectedColumn, setSelectedColumn] = useState<Header | null>(null);

  // Non-benchmark edges for Filters and Attributes components (attribute + temporal columns)
  const [nonBenchmarkEdges, setNonBenchmarkEdges] = useState<ObservationColumnProto[]>([]);
	// Benchmark entities for Benchmarks component
  const [benchmarkEntities, setBenchmarkEntities] = useState<BenchmarkEntityProto[]>([]);

	// Data table states
	const [dataTableData, setDataTableData] = useState<NodeInsights | null>(null);
	const [isLoadingDataTable, setIsLoadingDataTable] = useState<boolean>(false);
	const [errorDetails, setErrorDetails] = useState<ErrorDetailProto[] | null>(null);

  // Save modal state
  const { isOpen: isSaveModalOpen, onOpen: onSaveModalOpen, onClose: onSaveModalClose } = useDisclosure();
  const [isSaving, setIsSaving] = useState<boolean>(false);

  // Check if sections should be disabled (no metrics selected)
  const isSectionsDisabled = selectedMetrics.length === 0;

  // Get selected metric IDs for fetching details
  const selectedMetricIds = useMemo(() => 
    selectedMetricHeaders.map(h => h.id || "").filter(Boolean),
    [selectedMetricHeaders]
  );

  // Get available columns (not already selected)
  const availableColumns = useMemo(() => {
    return nonBenchmarkEdges.filter(
      (col) => !selectedColumns.some((sc) => sc.column_id === col.column_id)
    );
  }, [nonBenchmarkEdges, selectedColumns]);

  // Toggle metrics expansion
  const toggleMetricsExpansion = () => {
    setIsMetricsExpanded(!isMetricsExpanded);
  };

  // Handle section click
  const handleSectionClick = (section: SectionType) => {
    if (isSectionsDisabled) return;
    // Toggle: if clicking the active section, close it; otherwise, open the new section
    setActiveSection(activeSection === section ? null : section);
  };

  // Handle tab change
  const handleTabChange = (index: number) => {
    // Only allow switching to Data Table if enabled
    if (index === 1 && !isDataTableEnabled) return;
    setActiveTab(index);
  };

  // Handle Get Data click - enables and switches to Data Table tab
  const handleGetData = async() => {
		if(selectedMetrics.length === 0) {
			showToast("Please select at least one metric", "", "error");
			return;
		}
		setIsLoadingDataTable(true);
		setActiveTab(1);
    const request = KGObservationProto.create({
			metrics: selectedMetrics || [],
      benchmarks: benchmarks || [],
      filters: filters || [],
      columns: selectedColumns || [],
      benchmark_columns: benchmarkColumnHashes || []
		});
		const response = await getKGObservationData([request]);
		if(response) {
			setDataTableData({
				current_node: AnalysisTreeNodeProto.create({
					kg_observation: response[0]?.kg_observation_data?.kg_observation,
					kg_observation_data: response[0]?.kg_observation_data,
				}),
				insights: undefined,
			});
			setErrorDetails(response[0].error_details);
			setIsLoadingDataTable(false);
			showToast("Data fetched successfully", "", "success");
			setIsDataTableEnabled(true);
		} else {
			showToast("Failed to fetch data", "", "error");
		}
  };

  // Handle close table - switch back to Configurator, clear data, disable Data Table tab
  const handleCloseTable = () => {
    setActiveTab(0);
    setDataTableData(null);
    setErrorDetails(null);
    setIsDataTableEnabled(false);
  };

  // Fetch all metrics on mount
  useEffect(() => {
    const fetchMetrics = async () => {
      setIsLoadingMetrics(true);
      try {
        const metrics = await listMetrics("");
        if (metrics) {
          setListMetricsHeaders(metrics);
        }
      } catch (error) {
        showToast("Error fetching metrics", "", "error");
      } finally {
        setIsLoadingMetrics(false);
      }
    };
    fetchMetrics();
  }, []);

  // Load existing data query when dataQueryId is provided, or reset for new query
  useEffect(() => {
    const loadExistingDataQuery = async () => {
      // If no dataQueryId, reset to fresh state for new query
      if (!dataQueryId) {
        setExistingDataQueryId(undefined);
        setName("");
        setDescription("");
        setSelectedMetricHeaders([]);
        setSelectedMetrics([]);
        setFilters([]);
        setSelectedColumns([]);
        setBenchmarks([]);
        setBenchmarkColumnHashes([]);
        setIsMetricsExpanded(true);
        setDataTableData(null);
        setErrorDetails(null);
        setIsDataTableEnabled(false);
        setIsLoadingDataQuery(false);
        return;
      }
			setActiveTab(0); // Switch to Configuration tab
			
      setIsLoadingDataQuery(true);

      try {
        // Fetch the data query
        const dataQuery = await getPlan(dataQueryId);
        if (!dataQuery) {
          showToast("Failed to load data query", "", "error");
          setIsLoadingDataQuery(false);
          return;
        }

        // Set basic info
        setName(dataQuery.header?.name || "");
        setDescription(dataQuery.header?.description || "");
        setExistingDataQueryId(dataQuery.header?.id);

        // Get metric IDs from root_observation
        const metricIds = dataQuery.root_observation?.metrics
          ?.map((m) => m.header?.id)
          .filter((id): id is string => !!id) || [];

        if (metricIds.length > 0) {
          // Fetch metrics with edges
          const metricsWithEdges = await getAllMetrics(metricIds, true);
          
          // Set selected metric headers (for UI)
          const metricHeaders = metricsWithEdges.map((m) => m.header).filter((h): h is Header => !!h);
          setSelectedMetricHeaders(metricHeaders);
          
          // Set selected metrics (full with edges)
          setSelectedMetrics(metricsWithEdges);
        }

        // Set filters
        setFilters(dataQuery.root_observation?.filters || []);

        // Set columns (attributes)
        setSelectedColumns(dataQuery.root_observation?.columns || []);

        // Set benchmarks
        setBenchmarks(dataQuery.root_observation?.benchmarks || []);

        // Find root step and get analysis_columns
        const rootStep = dataQuery.analysis_steps?.find((step) => step.id === dataQuery.root_step_id);
        if (rootStep && rootStep.analysis_columns) {
          // Bifurcate benchmark and analysis column hashes
          const { benchmarkColumnHashes: bifurcatedBenchmarkHashes } = 
            bifurcateBenchmarkColumnHashesAndAnalysisColumns(
              rootStep.analysis_columns,
              dataQuery.root_observation?.metrics,
              dataQuery.root_observation?.benchmarks,
              dataQuery.root_observation?.columns
            );
          
          setBenchmarkColumnHashes(bifurcatedBenchmarkHashes);
        }

        // Collapse metrics section since we already have them loaded
        setIsMetricsExpanded(false);

      } catch (error) {
        console.error("Error loading data query:", error);
        showToast("Error loading data query", "", "error");
      } finally {
        setIsLoadingDataQuery(false);
      }
    };

    loadExistingDataQuery();
  }, [dataQueryId]);

  // When selected metric IDs change, fetch full metric details with edges
  useEffect(() => {
    const fetchMetricDetails = async () => {
      if (selectedMetricIds.length === 0) {
        setSelectedMetrics([]);
        return;
      }

      // Check if we already have all the metrics loaded (prevents duplicate fetch after loading existing data query)
      const existingMetricIds = selectedMetrics.map(m => m.header?.id).filter(Boolean);
      const allMetricsAlreadyLoaded = selectedMetricIds.every(id => existingMetricIds.includes(id)) 
        && existingMetricIds.length === selectedMetricIds.length;
      
      if (allMetricsAlreadyLoaded) {
        return;
      }

      setIsFetchingMetricDetails(true);
      try {
        // Use getAllMetrics for batch fetching
        const metricsWithEdges = await getAllMetrics(selectedMetricIds, true);
        setSelectedMetrics(metricsWithEdges);
      } catch (error) {
        showToast("Error fetching metric details", "", "error");
      } finally {
        setIsFetchingMetricDetails(false);
      }
    };

    fetchMetricDetails();
  }, [selectedMetricIds]);

  // When selectedMetrics change, compute merged edges and nonBenchmarkEdges
  useEffect(() => {
    if (selectedMetrics.length === 0) {
      setBenchmarkEntities([]);
      setNonBenchmarkEdges([]);
      setSelectedColumns([]); // Clear selected columns when metrics change
      // Clear active section when metrics are deselected
      setActiveSection(null);
      return;
    }

    const merged = mergeMetricEdgesByType(selectedMetrics);

    // Get growth benchmarks
    const growthBenchmarks = getKGGrowthBenchmarks([]);
    // Combine with benchmark entities from metrics
    const allBenchmarks = [
      ...(merged?.benchmarkEntities || []),
      ...growthBenchmarks,
    ];
    setBenchmarkEntities(allBenchmarks);

    // Create nonBenchmarkEdges (ObservationColumnProto[]) from attribute and temporal headers
    const nonBenchmarks: ObservationColumnProto[] = [];

    // Add attribute columns
    merged.attributeHeaders.forEach((header) => {
      nonBenchmarks.push(
        ObservationColumnProto.create({
          column_id: header.id,
          kg_column_header: {
            ...header,
            column_type: ColumnType.ATTRIBUTE,
          },
        })
      );
    });

    // Add temporal columns
    merged.temporalHeaders.forEach((header) => {
      nonBenchmarks.push(
        ObservationColumnProto.create({
          column_id: header.id,
          kg_column_header: {
            ...header,
            column_type: ColumnType.TEMPORAL,
          },
        })
      );
    });

    setNonBenchmarkEdges(nonBenchmarks);
  }, [selectedMetrics]);

  // Handle opening save modal
  const handleOpenSaveModal = () => {
    onSaveModalOpen();
  };

	// Handle save data query - save the data query
  const handleSaveDataQuery = async() => {
    // Validate name is required
    if (!name.trim()) {
      showToast("Name is required", "", "error");
      return;
    }

    setIsSaving(true);
    
    // Use existing IDs if editing, otherwise generate new ones
    const dataQueryHeaderId = existingDataQueryId || uuidv4();
		const rootStepId = uuidv4();
    
    const request = AnalysisPlanProto.create({
      header: Header.create({
        id: dataQueryHeaderId,
        name: name.trim(),
        description: description.trim(),
        type: MetadataTypeEnum.ANALYSIS_PLAN
      }),
      root_observation: KGObservationProto.create({
        metrics: selectedMetrics || [],
        filters: filters || [],
        columns: selectedColumns || [],
        benchmarks: benchmarks || [],
      }),
      root_step_id: rootStepId,
      analysis_steps: [AnalysisStepProto.create({
        id: rootStepId,
        step_label: "Root",
        analysis_columns: benchmarkColumnHashes || [],
      })],
    });
    
    const response = await createOrUpdatePlan(request);
    setIsSaving(false);
    
    if(response) {
      // Update existingDataQueryId so future saves will update instead of create
      if (!existingDataQueryId) {
        setExistingDataQueryId(dataQueryHeaderId);
      }
      onSaveModalClose();
      showToast("Data query saved successfully", "", "success");
    } else {
      showToast("Failed to save data query", "", "error");
    }
  };

  // Handle metric checkbox change - for MetricSelection component
  const handleMetricChange = (_event: React.ChangeEvent<HTMLInputElement>, metricId: string) => {
    setSelectedMetricHeaders((prev) => {
      const isSelected = prev.some(h => h.id === metricId);
      if (isSelected) {
        return prev.filter((h) => h.id !== metricId);
      } else {
        const metricHeader = listMetricsHeaders.find(h => h.id === metricId);
        if (metricHeader) {
          return [...prev, metricHeader];
        }
        return prev;
      }
    });
  };

  // Handle select all / deselect all
  const handleSelectAll = () => {
    const allSelected = listMetricsHeaders.every(metric =>
      selectedMetricHeaders.some(h => h.id === metric.id)
    );
    
    if (allSelected) {
      setSelectedMetricHeaders([]);
    } else {
      setSelectedMetricHeaders([...listMetricsHeaders]);
    }
  };

  // Handle clear all selections
  const handleClearSelections = () => {
    setSelectedMetricHeaders([]);
  };

  // Handle column selection from dropdown
  const handleSelectColumn = (column: ObservationColumnProto) => {
    if (selectedColumns.some((c) => c.column_id === column.column_id)) return;

    // For temporal columns, add with default time bucket (can be edited later)
    if (column.kg_column_header?.column_type === ColumnType.TEMPORAL) {
      setSelectedColumns((prev) => [...prev, { ...column, time_bucket: TimeBucket.MONTHLY }]);
    } else {
      setSelectedColumns((prev) => [...prev, column]);
    }
    setSelectedColumn(null);
  };

  // Remove a column from selection
  const handleRemoveColumn = (columnId: string) => {
    setSelectedColumns((prev) => prev.filter((column) => column.column_id !== columnId));
  };

  // Handle time bucket change for temporal columns
  const handleTimeBucketChange = (columnId: string, timeBucket: TimeBucket) => {
    setSelectedColumns((prev) =>
      prev.map((column) =>
        column?.column_id === columnId
          ? { ...column, time_bucket: timeBucket }
          : column
      )
    );
  };

  // Sidebar navigation items configuration
  // Only show badges for selected/applied items, not available items
  const sidebarItems = [
    {
      id: "filters" as SectionType,
      icon: FaFilter,
      label: "Filters",
      badge: filters.length > 0 ? filters.length : null,
    },
    {
      id: "attributes" as SectionType,
      icon: FiGrid,
      label: "Attributes",
      badge: selectedColumns.length > 0 ? selectedColumns.length : null,
    },
    {
      id: "benchmarks" as SectionType,
      icon: FiTarget,
      label: "Benchmarks",
      badge: benchmarkColumnHashes.length > 0 ? benchmarkColumnHashes.length : null,
    },
  ];

  // Render the content based on active section
  const renderSectionContent = () => {
    if (!activeSection) {
      return (
        <Center h="100%" flexDirection="column" py={12}>
          <Icon as={FiDatabase} boxSize={16} color="gray.300" mb={4} />
          <Text fontSize="lg" color="gray.500" fontWeight="500" mb={2}>
            Select a section to configure
          </Text>
          <Text fontSize="sm" color="gray.400">
            Choose from Filters, Attributes, Benchmarks, or Analysis
          </Text>
        </Center>
      );
    }

    switch (activeSection) {
      case "filters":
        return (
          <Box>
            <Text fontSize="lg" fontWeight="700" color="purple.700" mb={4} textAlign="left">
              Configure Filters
            </Text>
            <Filters
              filters={filters}
              setFilters={setFilters}
              nonBenchmarkEdges={nonBenchmarkEdges}
              selectedMetrics={selectedMetrics}
            />
          </Box>
        );
      case "attributes":
        return (
          <Box>
            <Text fontSize="lg" fontWeight="700" color="purple.700" mb={4} textAlign="left">
              Split by Attributes
            </Text>
            <Text fontSize="sm" color="gray.600" mb={4} textAlign="left">
              Select business dimensions to group your data analysis
            </Text>

            {/* Column Dropdown */}
            <FormControl mb={4}>
              <Menu closeOnSelect={true}>
                <MenuButton
                  as={Button}
                  rightIcon={<ChevronDownIcon />}
                  w="100%"
                  textAlign="left"
                  variant="outline"
                  colorScheme="purple"
                  isDisabled={availableColumns.length === 0}
                  fontWeight="600"
                >
                  {selectedColumn
                    ? selectedColumn.name || "Unnamed Column"
                    : availableColumns.length === 0
                    ? "No columns available"
                    : "Select Attribute"}
                </MenuButton>
                <Portal>
                  <MenuList maxH="250px" overflowY="auto" zIndex={1500}>
                    {availableColumns?.map((column, index) => (
                      <MenuItem
                        key={index}
                        onClick={() => handleSelectColumn(column)}
                        _hover={{ bg: "purple.50" }}
                        fontWeight="600"
                      >
                        {column.kg_column_header?.name}
                      </MenuItem>
                    ))}
                  </MenuList>
                </Portal>
              </Menu>
            </FormControl>

            {/* Display Selected Columns */}
            {selectedColumns?.length > 0 && (
              <Box>
                <Stack spacing={2}>
                  {selectedColumns?.map((column) => (
                    <Box
                      key={column.column_id}
                      borderWidth="1px"
                      borderRadius="md"
                      p={2}
                      backgroundColor="gray.50"
                      borderColor="purple.700"
                    >
                      <Flex justifyContent="space-between" alignItems="center">
                        <Text fontWeight="bold" fontSize="sm" mb={0} ml={1}>
                          Split by {column?.kg_column_header?.name}
                        </Text>
                        <IconButton
                          aria-label="Remove column"
                          icon={<Icon as={CloseIcon} boxSize={3} />}
                          size="xs"
                          onClick={() => handleRemoveColumn(column?.column_id || "")}
                          variant="ghost"
                          colorScheme="red"
                        />
                      </Flex>

                      {/* Inline time bucket selection for temporal columns */}
                      {column?.kg_column_header?.column_type === ColumnType.TEMPORAL && (
                        <Box mt={2}>
                          <Text fontSize="xs" color="gray.600" mb={1} ml={1} fontWeight="700" textAlign="left">
                            Time Buckets
                          </Text>
                          <Flex gap={1} flexWrap="wrap" p={1}>
                            {Object?.entries(TimeBucket)
                              ?.filter(
                                ([key, value]) =>
                                  typeof value === "number" && value !== TimeBucket.UNRECOGNIZED
                              )
                              .map(([key, value]) => (
                                <Button
                                  key={key}
                                  size="xs"
                                  variant={column.time_bucket === value ? "solid" : "outline"}
                                  colorScheme="purple"
                                  onClick={() =>
                                    handleTimeBucketChange(column?.column_id || "", value as TimeBucket)
                                  }
                                  bg={column.time_bucket === value ? "purple.700" : "white"}
                                >
                                  {formatToSentenceCase(key)}
                                </Button>
                              ))}
                          </Flex>
                        </Box>
                      )}
                    </Box>
                  ))}
                </Stack>
              </Box>
            )}

            {/* Empty state when no columns selected */}
            {selectedColumns.length === 0 && availableColumns.length > 0 && (
              <Center py={6}>
                <VStack spacing={2}>
                  <Icon as={AiFillPieChart} boxSize={10} color="gray.300" />
                  <Text fontSize="sm" color="gray.500">
                    No attributes selected yet
                  </Text>
                  <Text fontSize="xs" color="gray.400">
                    Use the dropdown above to select attributes
                  </Text>
                </VStack>
              </Center>
            )}
          </Box>
        );
      case "benchmarks":
        return (
          <Box>
            <Text fontSize="lg" fontWeight="700" color="purple.700" mb={4} textAlign="left">
              Define Benchmarks
            </Text>
            <Text fontSize="sm" color="gray.600" mb={4} textAlign="left">
              Configure benchmarks for comparison analysis
            </Text>
            <Benchmarks
              benchmarkEntities={benchmarkEntities}
              selectedBenchmarks={benchmarks}
              setSelectedBenchmarks={setBenchmarks}
              benchmarkColumnHashes={benchmarkColumnHashes}
              setBenchmarkColumnHashes={setBenchmarkColumnHashes}
              selectedColumns={selectedColumns}
              selectedMetrics={selectedMetrics}
              filters={filters}
            />
          </Box>
        );
      default:
        return null;
    }
  };

  // Render Configurator Tab Content
  const renderConfiguratorContent = () => (
    <Box height="100%" overflowY="auto" p={6}>
      <VStack spacing={6} align="stretch" maxW="1600px" mx="auto">
        {/* Metrics Selection Section - Collapsible */}
        <Box
          borderWidth="1px"
          borderRadius="xl"
          borderColor="purple.600"
          boxShadow="sm"
          _hover={isMetricsExpanded ? { bg: "transparent" } : { bg: "purple.50" }}
        >
          {/* Header - Always Visible */}
          <HStack
            spacing={2}
            cursor="pointer"
            onClick={toggleMetricsExpansion}
            p={4}
            borderRadius="xl"
            justifyContent="space-between"
          >
            <HStack spacing={3}>
              <Icon as={MdBarChart} boxSize={6} color="purple.600" />
              <Text fontWeight="700" fontSize="lg" mb={0} color="purple.600">
                Select Metrics
              </Text>
              {/* Show count badge when collapsed */}
              {!isMetricsExpanded && selectedMetricHeaders.length > 0 && (
                <Badge bg="purple.600" color="white" borderRadius="full" px={3} py={1}>
                  {selectedMetricHeaders.length} {selectedMetricHeaders.length > 1 ? "metrics" : "metric"}
                </Badge>
              )}
            </HStack>
            {/* Show minimize button when expanded */}
            {isMetricsExpanded && (
              <IconButton
                aria-label="Minimize section"
                icon={<Icon as={FaRegWindowMinimize} boxSize={4} color="purple.600" />}
                size="sm"
                variant="ghost"
                color="purple.600"
                _hover={{ bg: "purple.100" }}
                onClick={(e) => {
                  e.stopPropagation();
                  toggleMetricsExpansion();
                }}
              />
            )}
          </HStack>

          {/* Help text when collapsed */}
          {!isMetricsExpanded && (
            <Box
              px={4}
              pb={4}
              cursor="pointer"
              onClick={toggleMetricsExpansion}
              _hover={{ bg: "purple.50" }}
              borderRadius="xl"
            >
              <Text fontSize="sm" color="gray.600" mb={0} textAlign="left">
                Choose metrics and KPIs Anko can analyze
              </Text>
            </Box>
          )}

          {/* Collapsible Content */}
          <Collapse in={isMetricsExpanded} animateOpacity>
            <Box p={4} pt={0}>
              {isLoadingMetrics ? (
                <Center py={6}>
                  <Spinner size="md" color="purple.500" />
                  <Text ml={3} color="gray.600">
                    Loading metrics...
                  </Text>
                </Center>
              ) : (
                <MetricSelection
                  metricList={listMetricsHeaders}
                  selectedMetricHeader={selectedMetricHeaders}
                  onMetricChange={handleMetricChange}
                  onSelectAll={handleSelectAll}
                  onClearSelections={handleClearSelections}
                  isLoading={isFetchingMetricDetails}
                  width="100%"
                />
              )}
            </Box>
          </Collapse>
        </Box>

        {/* Configuration Section: Sidebar + Content Layout */}
        <Box
          borderWidth="1px"
          borderRadius="xl"
          borderColor={isSectionsDisabled ? "gray.200" : "purple.600"}
          overflow="hidden"
          opacity={isSectionsDisabled ? 0.6 : 1}
          transition="all 0.2s"
        >
          <Flex h="100%">
            {/* Left Sidebar Navigation */}
            <Box
              w="180px"
              borderRightWidth="1px"
              borderColor={isSectionsDisabled ? "gray.200" : "purple.200"}
              bg={isSectionsDisabled ? "gray.50" : "purple.50"}
              py={2}
            >
              <VStack spacing={1} align="stretch">
                {sidebarItems.map((item) => {
                  const isActive = activeSection === item.id;
                  return (
                    <Box
                      key={item.id}
                      px={3}
                      py={3}
                      cursor={isSectionsDisabled ? "not-allowed" : "pointer"}
                      onClick={() => handleSectionClick(item.id)}
                      bg={isActive ? "purple.600" : "transparent"}
                      borderRadius="lg"
                      _hover={
                        isSectionsDisabled
                          ? {}
                          : {
                              bg: isActive ? "purple.600" : "purple.100",
                            }
                      }
                      transition="all 0.15s"
                    >
                      <HStack spacing={3} justify="space-between">
                        <HStack spacing={2}>
                          <Icon
                            as={item.icon}
                            boxSize={4}
                            color={
                              isSectionsDisabled
                                ? "gray.400"
                                : isActive
                                ? "white"
                                : "purple.600"
                            }
                          />
                          <Text
                            fontSize="sm"
                            fontWeight="600"
                            color={
                              isSectionsDisabled
                                ? "gray.400"
                                : isActive
                                ? "white"
                                : "purple.700"
                            }
                            m={0}
                          >
                            {item.label}
                          </Text>
                        </HStack>
                        {/* Badge */}
                        {item.badge !== null && !isSectionsDisabled && (
                          <Badge
                            bg={isActive ? "white" : "purple.600"}
                            color={isActive ? "purple.600" : "white"}
                            borderRadius="full"
                            fontSize="xs"
                            px={2}
                            minW="20px"
                            textAlign="center"
                          >
                            {item.badge}
                          </Badge>
                        )}
                      </HStack>
                    </Box>
                  );
                })}
              </VStack>
            </Box>

            {/* Right Content Area */}
            <Box flex={1} p={6} bg="white">
              {isSectionsDisabled ? (
                <Center h="100%" flexDirection="column">
                  <Icon as={MdBarChart} boxSize={16} color="gray.300" mb={4} />
                  <Text fontSize="lg" color="gray.500" fontWeight="500" mb={2}>
                    No metrics selected
                  </Text>
                  <Text fontSize="sm" color="gray.400">
                    Please select at least one metric to configure filters, attributes, benchmarks, and analysis
                  </Text>
                </Center>
              ) : (
                renderSectionContent()
              )}
            </Box>
          </Flex>
        </Box>
      </VStack>
    </Box>
  );

  // Render Data Table Tab Content
  const renderDataTableContent = () => {
    // Show loading spinner
    if (isLoadingDataTable) {
      return (
        <Box height="100%" p={6}>
          <Center h="100%" flexDirection="column">
            <Spinner size="xl" color="purple.500" thickness="4px" mb={4} />
            <Text fontSize="lg" color="gray.600" fontWeight="500">
              Fetching data...
            </Text>
            <Text fontSize="sm" color="gray.400" mt={2}>
              Please wait while we load your data
            </Text>
          </Center>
        </Box>
      );
    }

    // Show Data Table if data is available
    if (dataTableData) {
      return (
        <Box height="100%" p={6} overflowY="auto">
          <DataTableComponent
            observationData={dataTableData}
            error={errorDetails || undefined}
            onCloseTable={handleCloseTable}
            isSelected={true}
          />
        </Box>
      );
    }

    // Empty state (shouldn't normally be visible since tab is disabled when no data)
    return (
      <Box height="100%" p={6}>
        <Center h="100%" flexDirection="column">
          <Icon as={FaTable} boxSize={16} color="gray.300" mb={4} />
          <Text fontSize="lg" color="gray.500" fontWeight="500" mb={2}>
            Data Table
          </Text>
          <Text fontSize="sm" color="gray.400">
            Click "Get Data" to load your data
          </Text>
        </Center>
      </Box>
    );
  };

  // Show loading state while fetching existing data query
  if (isLoadingDataQuery) {
    return (
      <Box height="100%" bg="white">
        <Center h="100%" flexDirection="column">
          <Spinner size="xl" color="purple.500" thickness="4px" mb={4} />
          <Text fontSize="lg" color="gray.600" fontWeight="500">
            Loading data query...
          </Text>
          <Text fontSize="sm" color="gray.400" mt={2}>
            Please wait while we load your configuration
          </Text>
        </Center>
      </Box>
    );
  }

  return (
    <Box height="100%" bg="white">
      <Tabs
        colorScheme="purple"
        variant="unstyled"
        size="md"
        height="100%"
        index={activeTab}
        onChange={handleTabChange}
      >
        {/* Header with Back Button and Pill Tabs */}
        <Flex
          p={4}
          borderBottom="1px"
          borderColor="gray.200"
          bg="white"
          align="center"
          justify="space-between"
          w="100%"
        >
          {/* Left Side: Back Button + Data Query Name */}
          <HStack spacing={4} flex="0 0 auto">
            {/* Data Query Name */}
            <HStack spacing={3}>
              <Icon as={FiDatabase} boxSize={7} color="purple.700" />
              <Text
                fontSize="lg"
                fontWeight="bold"
                color="gray.800"
                mb={0}
                align="left"
                noOfLines={1}
              >
                {name || "New Data Query"}
              </Text>
              {/* Info icon with description tooltip */}
              {description && (
                <Tooltip 
                  label={description} 
                  placement="bottom"
                  hasArrow
                  bg="gray.700"
                  color="white"
                  px={3}
                  py={2}
                  borderRadius="md"
                  fontSize="sm"
                  maxW="300px"
                >
                  <Box display="inline-flex" cursor="pointer">
                    <Icon as={InfoIcon} boxSize={4} color="gray.500" _hover={{ color: "purple.600" }} />
                  </Box>
                </Tooltip>
              )}
            </HStack>
          </HStack>
          
          {/* Right Side: Pill-style Tabs */}
          <TabList gap={3} flex="0 0 auto" justifyContent="flex-end">
            <Tab
              bg="gray.100"
              borderRadius="full"
              px={6}
              py={3}
              fontSize="md"
              fontWeight="600"
              color="gray.600"
              _selected={{
                bg: "purple.600",
                color: "white",
                boxShadow: "sm",
              }}
              _hover={{
                bg: "gray.200",
                transform: "translateY(-1px)",
              }}
              transition="all 0.2s"
            >
              <HStack spacing={2}>
                <Icon as={LuScrollText} boxSize={5} />
                <Text mb={0}>Configuration</Text>
              </HStack>
            </Tab>
            <Tab
              isDisabled={!isDataTableEnabled}
              bg="gray.100"
              borderRadius="full"
              px={6}
              py={3}
              fontSize="md"
              fontWeight="600"
              color="gray.600"
              _selected={{
                bg: "purple.600",
                color: "white",
                boxShadow: "sm",
              }}
              _hover={{
                bg: isDataTableEnabled ? "gray.200" : "gray.100",
                transform: isDataTableEnabled ? "translateY(-1px)" : "none",
              }}
              _disabled={{
                opacity: 0.5,
                cursor: "not-allowed",
              }}
              transition="all 0.2s"
            >
              <HStack spacing={2}>
                <Icon as={FaTable} boxSize={5} />
                <Text mb={0}>Data Table</Text>
              </HStack>
            </Tab>
						<Tab
							bg="gray.100"
							borderRadius="full"
							px={6}
							py={3}
							fontSize="md"
							fontWeight="600"
							color="gray.600"
							_selected={{
								bg: "purple.600",
								color: "white",
								boxShadow: "sm",
							}}
							_hover={{
								bg: "gray.200",
								transform: "translateY(-1px)",
							}}
							transition="all 0.2s"
						>
							<HStack spacing={2}>
								<Icon as={FaHistory} boxSize={5} />
								<Text mb={0}>Saved Queries</Text>
							</HStack>
						</Tab>
          </TabList>
        </Flex>

        <TabPanels height="calc(100% - 73px)" bg="white">
          <TabPanel height="100%" p={0}>
            {renderConfiguratorContent()}
          </TabPanel>
          <TabPanel height="100%" p={0}>
            {renderDataTableContent()}
          </TabPanel>
          <TabPanel height="100%" p={0}>
            <Box height="100%" overflowY="auto" bg="bg.surface">
              <Box maxW="800px" mx="auto" px={8} py={6}>
                <DataQueryList />
              </Box>
            </Box>
          </TabPanel>
        </TabPanels>
      </Tabs>

      {/* Fixed Overlay Buttons at Bottom */}
      <Box
        position="fixed"
        bottom="20px"
        right="20px"
        bg="white"
        border="1px"
        borderColor="gray.200"
        borderRadius="lg"
        boxShadow="lg"
        p={3}
        zIndex={1000}
      >
        <HStack spacing={3} justify="space-around">
          {/* Get Data Button */}
          <Button
            leftIcon={<Icon as={FaPlay} boxSize={3} />}
            onClick={handleGetData}
            size="md"
            colorScheme="purple"
            bg="purple.600"
            _hover={{ bg: "purple.700" }}
          >
            Get Data
          </Button>

          {/* Save Button */}
          <Button 
            colorScheme="green" 
            size="md"
            onClick={handleOpenSaveModal}
            leftIcon={<Icon as={FaCheck} boxSize={3} />}
          >
            Save
          </Button>
        </HStack>
      </Box>

      {/* Save Data Query Modal */}
      <Modal isOpen={isSaveModalOpen} onClose={onSaveModalClose} isCentered>
        <ModalOverlay />
        <ModalContent>
          <ModalHeader>Save Data Query</ModalHeader>
          <ModalCloseButton />
          <ModalBody>
            <VStack spacing={4} align="stretch">
              <FormControl isRequired>
                <FormLabel fontSize="sm" fontWeight="600" color="gray.700">
                  Name
                </FormLabel>
                <Input
                  value={name}
                  onChange={(e) => setName(e.target.value)}
                  placeholder="Enter data query name"
                  bg="white"
                  border="1px"
                  borderColor="gray.300"
                  _hover={{ borderColor: "gray.400" }}
                  _focus={{
                    borderColor: "purple.500",
                    boxShadow: "0 0 0 1px var(--chakra-colors-purple-500)",
                  }}
                />
              </FormControl>
              <FormControl>
                <FormLabel fontSize="sm" fontWeight="600" color="gray.700">
                  Description
                </FormLabel>
                <Textarea
                  value={description}
                  onChange={(e) => setDescription(e.target.value)}
                  placeholder="Enter description (optional)"
                  bg="white"
                  border="1px"
                  borderColor="gray.300"
                  rows={3}
                  resize="none"
                  _hover={{ borderColor: "gray.400" }}
                  _focus={{
                    borderColor: "purple.500",
                    boxShadow: "0 0 0 1px var(--chakra-colors-purple-500)",
                  }}
                />
              </FormControl>
            </VStack>
          </ModalBody>
          <ModalFooter>
            <Button variant="ghost" mr={3} onClick={onSaveModalClose}>
              Cancel
            </Button>
            <Button
              colorScheme="green"
              onClick={handleSaveDataQuery}
              isLoading={isSaving}
              loadingText="Saving..."
              isDisabled={!name.trim()}
            >
              Save
            </Button>
          </ModalFooter>
        </ModalContent>
      </Modal>
    </Box>
  );
};

export default DataConfiguratorContainer;
